import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  AccountTree as SopIcon,
  OpenInNew as DetailsIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import PageContentLayout from "../components/layout/PageContentLayout";
import apiService from "../services/apiService";
import { crmColors } from "../theme/colors";

const BAND_COLOR = {
  "At risk": crmColors.error.main,
  Dormant: crmColors.secondary.main,
  "Setup gap": crmColors.warning.main,
  "Demo ready": crmColors.info?.main || "#0288D1",
  Operating: crmColors.primary.main,
  "Deep use": crmColors.success.main,
  Unknown: crmColors.secondary.light,
};

const DEFAULT_STAGES = [
  { stage: "1", label: "Account live" },
  { stage: "2", label: "Academic setup" },
  { stage: "3", label: "Fee setup" },
  { stage: "4", label: "First operating week" },
  { stage: "5", label: "Parent habit" },
  { stage: "6", label: "Exam cycle" },
  { stage: "7", label: "Save and expand" },
];

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    bgcolor: "#fff",
  },
};

//Created By Cursor
const ScoreBar = ({ label, value, max = 40, color }) => (
  <Box sx={{ minWidth: 0, flex: 1 }}>
    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.3 }}>
      <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: crmColors.text.secondary }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 10.5, fontWeight: 800 }}>
        {value}/{max}
      </Typography>
    </Stack>
    <LinearProgress
      variant="determinate"
      value={Math.min(100, (Number(value) / max) * 100)}
      sx={{
        height: 6,
        borderRadius: 99,
        bgcolor: alpha(color, 0.12),
        "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 99 },
      }}
    />
  </Box>
);

//Created By Cursor
const Section = ({ title, hint, children }) => (
  <Box
    sx={{
      p: { xs: 1.5, sm: 1.75 },
      borderRadius: "14px",
      border: `1px solid ${crmColors.border.default}`,
      background: `linear-gradient(180deg, ${alpha(crmColors.primary.main, 0.03)} 0%, ${
        crmColors.background.paper
      } 100%)`,
    }}
  >
    <Box sx={{ mb: 1.15 }}>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 800,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: crmColors.text.primary,
        }}
      >
        {title}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      ) : null}
    </Box>
    {children}
  </Box>
);

//Created By Cursor
const SchoolSopDetails = () => {
  const navigate = useNavigate();
  const { schoolCode: schoolCodeParam } = useParams();
  const schoolCode = decodeURIComponent(schoolCodeParam || "").trim();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [editStage, setEditStage] = useState("1");
  const [editTraining, setEditTraining] = useState("");
  const [editWorking, setEditWorking] = useState("");
  const [editNext, setEditNext] = useState("");

  const health = detail?.health || null;
  const tracks = detail?.tracks || [];
  const healthColor = BAND_COLOR[health?.health_band] || BAND_COLOR.Unknown;

  const stageOptions = useMemo(() => {
    const labels = detail?.stage_labels || health?.stage_labels || [];
    return Array.isArray(labels) && labels.length ? labels : DEFAULT_STAGES;
  }, [detail, health]);

  const load = useCallback(async () => {
    if (!schoolCode) {
      toast.error("Missing school code");
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await apiService.getSopSchoolDetail(schoolCode);
      if (res.success && res.data) {
        setDetail(res.data);
        const h = res.data.health || {};
        setEditStage(String(h.sop_stage || "1"));
        setEditTraining(h.training_status || h.master_training_status || "");
        setEditWorking(h.working_status || h.master_working_status || "");
        setEditNext(h.next_action || "");
      } else {
        toast.error(res.error || "Could not load school SOP detail");
        setDetail(null);
      }
    } catch (e) {
      toast.error(e?.message || "Could not load school SOP detail");
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [schoolCode]);

  useEffect(() => {
    load();
  }, [load]);

  //Created By Cursor
  const saveDetail = async () => {
    const code = health?.school_code || schoolCode;
    if (!code) return;
    setSaving(true);
    try {
      const res = await apiService.updateSopSchool({
        school_code: code,
        sop_stage: editStage,
        training_status: editTraining,
        working_status: editWorking,
        next_action: editNext,
      });
      if (res.success) {
        toast.success("SOP updated");
        setDetail(res.data);
      } else {
        toast.error(res.error || "Update failed");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContentLayout
      title={health?.school_name || "School SOP"}
      subtitle={
        health
          ? `${health.school_code || schoolCode}${
              health.admin_phone ? ` · ${health.admin_phone}` : ""
            }`
          : schoolCode || "Loading…"
      }
      barSubtitle={
        health
          ? `${health.health_band || "—"} · Score ${health.usability_score ?? "—"}/100`
          : "SOP school detail"
      }
      breadcrumbs={[
        {
          label: "Standard Operating Procedure",
          icon: <SopIcon sx={{ color: "#ffffff", fontSize: 18 }} />,
        },
        { label: health?.school_name || schoolCode || "Details" },
      ]}
      backTo="/sop"
      darkBar
      actions={
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DetailsIcon sx={{ fontSize: 16 }} />}
            disabled={!schoolCode}
            onClick={() =>
              navigate(`/schools/detail/${encodeURIComponent(schoolCode)}`)
            }
            sx={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: "10px",
              color: "#fff",
              borderColor: alpha("#fff", 0.45),
              "&:hover": {
                borderColor: "#fff",
                bgcolor: alpha("#fff", 0.1),
              },
            }}
          >
            School profile
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={saving || loading || !health}
            onClick={saveDetail}
            startIcon={
              saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />
            }
            sx={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: "10px",
              bgcolor: crmColors.energy.main,
              "&:hover": { bgcolor: crmColors.energy.dark },
            }}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </Stack>
      }
    >
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={28} sx={{ color: crmColors.primary.main }} />
        </Box>
      ) : !health ? (
        <Box
          sx={{
            textAlign: "center",
            py: 7,
            px: 2,
            borderRadius: "16px",
            border: `1px dashed ${crmColors.border.default}`,
            bgcolor: alpha(crmColors.primary.main, 0.03),
          }}
        >
          <Typography sx={{ fontWeight: 800 }}>School SOP not found</Typography>
          <Button
            sx={{ mt: 1.5, textTransform: "none", fontWeight: 800 }}
            onClick={() => navigate("/sop")}
          >
            Back to SOP
          </Button>
        </Box>
      ) : (
        <Stack spacing={1.75}>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={health.health_band}
              sx={{ fontWeight: 800, color: "#fff", bgcolor: healthColor }}
            />
            <Chip
              size="small"
              label={`Score ${health.usability_score}/100`}
              sx={{
                fontWeight: 700,
                bgcolor: alpha(crmColors.primary.main, 0.1),
                color: crmColors.primary.main,
              }}
            />
            <Chip
              size="small"
              label={health.subscription_status || "No subscription"}
              sx={{
                fontWeight: 700,
                bgcolor: alpha(crmColors.secondary.main, 0.1),
                color: crmColors.text.secondary,
              }}
            />
            <Chip
              size="small"
              label={`Stage ${health.sop_stage}. ${health.sop_stage_label || ""}`}
              sx={{
                fontWeight: 700,
                bgcolor: alpha(crmColors.logo.brand, 0.16),
                color: crmColors.logo.dark,
              }}
            />
          </Stack>

          <Section title="Usability scores" hint="Setup, usage & parent engagement">
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={4}>
                <ScoreBar
                  label="Setup"
                  value={health.setup_score}
                  max={40}
                  color={crmColors.primary.main}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ScoreBar
                  label="Usage"
                  value={health.usage_score}
                  max={40}
                  color={crmColors.success.main}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ScoreBar
                  label="Parent"
                  value={health.parent_score}
                  max={20}
                  color={crmColors.energy.main}
                />
              </Grid>
            </Grid>
            {health.next_action ? (
              <Typography
                sx={{ mt: 1.25, fontSize: 13, fontWeight: 700, color: crmColors.text.primary }}
              >
                Next · {health.next_action}
              </Typography>
            ) : null}
          </Section>

          <Section title="Track points" hint="Setup checklist & friction signals">
            <Stack spacing={0.85}>
              {tracks.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No track points for this school
                </Typography>
              ) : (
                tracks.map((t) => {
                  const isFriction = t.track_group === "Friction";
                  const ok = isFriction ? !t.is_done : !!t.is_done;
                  const label = isFriction
                    ? t.is_done
                      ? "Issue"
                      : "Clear"
                    : t.is_done
                      ? "Done"
                      : "Missing";
                  return (
                    <Box
                      key={t.track_code}
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        alignItems: "center",
                        px: 1.25,
                        py: 1,
                        borderRadius: "10px",
                        border: `1px solid ${crmColors.border.default}`,
                        bgcolor: "#fff",
                      }}
                    >
                      <Box sx={{ flex: "1 1 160px", minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 800, fontSize: 13 }}>
                          {t.track_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t.track_group}
                          {t.last_actor_id ? ` · ${t.last_actor_id}` : ""}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={label}
                        sx={{
                          height: 22,
                          fontWeight: 800,
                          fontSize: 11,
                          bgcolor: ok
                            ? alpha(crmColors.success.main, 0.12)
                            : alpha(crmColors.error.main, 0.12),
                          color: ok ? crmColors.success.dark : crmColors.error.main,
                        }}
                      />
                      <Typography
                        sx={{
                          flex: "1 1 180px",
                          fontSize: 12,
                          color: crmColors.text.secondary,
                        }}
                      >
                        {t.remark || t.healthy_rule || "—"}
                      </Typography>
                    </Box>
                  );
                })
              )}
            </Stack>
          </Section>

          <Section title="Update stage" hint="Training, working status & next action">
            <Grid container spacing={1.25}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="SOP stage"
                  value={editStage}
                  onChange={(e) => setEditStage(e.target.value)}
                  sx={fieldSx}
                >
                  {stageOptions.map((s) => (
                    <MenuItem key={s.stage} value={s.stage}>
                      {s.stage}. {s.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Training status"
                  value={editTraining}
                  onChange={(e) => setEditTraining(e.target.value)}
                  placeholder="e.g. Retrain scheduled"
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Working status"
                  value={editWorking}
                  onChange={(e) => setEditWorking(e.target.value)}
                  placeholder="e.g. Fee live"
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Next action"
                  value={editNext}
                  onChange={(e) => setEditNext(e.target.value)}
                  sx={fieldSx}
                />
              </Grid>
            </Grid>
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}>
              <Button
                variant="contained"
                disabled={saving}
                onClick={saveDetail}
                startIcon={
                  saving ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <SaveIcon />
                  )
                }
                sx={{
                  textTransform: "none",
                  fontWeight: 800,
                  borderRadius: "10px",
                  px: 2.25,
                  bgcolor: crmColors.energy.main,
                  boxShadow: `0 6px 16px ${alpha(crmColors.energy.main, 0.35)}`,
                  "&:hover": { bgcolor: crmColors.energy.dark },
                }}
              >
                {saving ? "Saving…" : "Save"}
              </Button>
            </Stack>
          </Section>
        </Stack>
      )}
    </PageContentLayout>
  );
};

export default SchoolSopDetails;
